import { pathOr } from 'ramda'

import { RootState } from '../reducers'
import { ActiveMatchInfo, AudienceType, ModeType } from '../types'
import { selectCurrentUser } from './blaster-peer-selectors'

const selectTrackIndex = (state: RootState) => state.session.trackIndex
const selectCurrentUsername = (state: RootState) => state.session.username
const selectCurrentBackgroundIndex = (state: RootState) => state.session.currentBackgroundIndex
const selectCurrentLyricsColorIndex = (state: RootState) => state.session.currentLyricsColorIndex
const selectCurrentBackgroundBlendMode = (state: RootState) =>
  state.session.currentBackgroundBlendMode
const selectCurrentThemeIndex = (state: RootState) => state.session.currentThemeIndex
const selectCurrentMatchSlug = (state: RootState) => state.session.currentMatchSlug
const selectVisualizationInfo = (state: RootState) => state.session.visualizationInfo
const selectCurrentBlaster = (state: RootState) => state.currentPlay.gamers
const selectActiveMatches = (state: RootState): ActiveMatchInfo[] =>
  state.session.matches.active || []
const selectArchivedMatches = (state: RootState) => state.session.matches.archived || []
const selectMatchInvites = (state: RootState) => state.session.matchInvites
const selectScoreVersion = (state: RootState) => {
  return state.session.prefs.scoreVersion
}
const selectAudience = (state: RootState) => state.session.prefs.audience
const getAudienceFlags = (audience: AudienceType) => {
  return {
    isPub: audience === 'pub',
    isCouch: audience === 'couch',
    isSolo: audience === 'solo',
    isProducer: audience === 'prod',
    isFollow: audience === 'follow',
  }
}
const selectNumPlayers = (state: RootState) => (state.session.isMulti ? 2 : 1)
const selectMode = (state: RootState) => state.session.mode
const getModeFlags = (mode: ModeType) => {
  return {
    isVizMode: mode === 'viz',
    isPlayMode: mode === 'play',
    isEditMode: mode === 'edit',
    isSpellMode: mode === 'spell',
  }
}
const selectIsShowSocial = (state: RootState) => state.session.isShowSocial
const selectIsShowGameSidebar = (state: RootState) => state.session.isShowGameSidebar
const selectIsShowSettings = (state: RootState) => state.session.isShowSettings
const selectSpotifyToken = (state: RootState) => state.session.spotifyToken
const selectHasSpotifyToken = (state: RootState) => state.session.spotifyToken.length > 0
const selectSpotifyPlayerState = (state: RootState) => state.session.spotifyPlayerState
const selectCurrentTrackImageUrl = (state: RootState) => {
  const images = pathOr(
    [],
    ['session', 'spotifyPlayerState', 'track_window', 'current_track', 'album', 'images'],
    state
  )

  return images.length ? (images[0] as Spotify.Image).url : ''
}
const selectIsSpotifyConnected = (state: RootState) => !!state.session.spotifyPlayerState

const selectCurrentSessionInfo = (state: RootState) => {
  const { username, currentMatchSlug, mode } = state.session
  const currentBlaster = state.currentPlay.gamers
  return { username, currentMatchSlug, mode, currentBlaster }
}

const selectIsCurrentOwnedMatch = (state: RootState) => {
  const currentMatchSlug = selectCurrentMatchSlug(state)
  const { ownedMatchSlug } = selectCurrentUser(state)
  return currentMatchSlug === ownedMatchSlug
}

const selectInviteForMatch = (matchSlugToFind: string) => (state: RootState) => {
  const activeMatches = state.session.matches.active
  if (activeMatches) {
    const activeMatch = activeMatches.find(
      ({ matchSlug }: ActiveMatchInfo) => matchSlug === matchSlugToFind
    )
    if (activeMatch) {
      return activeMatch.inviteKey
    }
  }
  const archivedMatch = state.session.matches.archived.find(
    ({ matchSlug }: ActiveMatchInfo) => matchSlug === matchSlugToFind
  )
  if (archivedMatch) {
    return archivedMatch.inviteKey
  }
  return '' // shouldn't happen
}

const selectPrefs = (state: RootState) => state.session.prefs

const selectUserInfo = (state: RootState) => {
  const { currUserAliasIndex, userAliases, username: currUsername } = state.session
  return { currUserAliasIndex, userAliases, currUsername }
}

const selectIsFullScreen = (state: RootState) => state.session.isFullscreen
const selectIsLandscape = (state: RootState) => state.session.isLandscape
const selectPlayerVisibility = (state: RootState) => state.session.playerVisibility

export {
  selectActiveMatches,
  selectArchivedMatches,
  selectAudience,
  getAudienceFlags,
  selectCurrentBackgroundBlendMode,
  selectCurrentBackgroundIndex,
  selectCurrentLyricsColorIndex,
  selectCurrentThemeIndex,
  selectVisualizationInfo,
  selectCurrentUsername,
  selectCurrentMatchSlug,
  selectCurrentBlaster,
  selectCurrentSessionInfo,
  selectCurrentTrackImageUrl,
  selectHasSpotifyToken,
  selectInviteForMatch,
  selectNumPlayers,
  selectIsCurrentOwnedMatch,
  selectIsFullScreen,
  selectIsLandscape,
  selectIsShowGameSidebar,
  selectIsShowSocial,
  selectIsShowSettings,
  selectIsSpotifyConnected,
  selectMatchInvites,
  selectMode,
  getModeFlags,
  selectPlayerVisibility,
  selectPrefs,
  selectScoreVersion,
  selectSpotifyPlayerState,
  selectSpotifyToken,
  selectTrackIndex,
  selectUserInfo,
}
